'use client';

import { X } from 'lucide-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Toast } from '../shared/Toast';
import Button from '../shared/Button';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Tooltip from '../shared/Tooltip';
import { InfoTooltip } from '../shared/InfoTooltip';

export default function EditProfileModal({ isOpen, onClose, userProfile, onSaveProfile }) {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [photo, setPhoto] = useState('');
	const [selectedImage, setSelectedImage] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const fileInputRef = useRef(null);
	const mouseDownInsideRef = useRef(false);
	const MAX_SIZE_BYTES = 1024 * 1024; // 1 MB
	const MAX_DIMENSION = 1024; // px

	useEffect(() => {
		if (isOpen && userProfile) {
			setFirstName(userProfile.firstName || '');
			setLastName(userProfile.lastName || '');
			setEmail(userProfile.email || '');
			setPhoto(userProfile.photo || '');
			// Reset image selection when modal opens
			setSelectedImage(null);
			setPreviewUrl(null);
		}
	}, [userProfile, isOpen]);

	useEffect(() => {
		if (previewUrl || photo) {
			const img = new Image();
			img.src = previewUrl || photo;
			img.onload = () => setIsImageLoaded(true);
		} else {
			setIsImageLoaded(true);
		}
	}, [photo, previewUrl]);

	const hasChangesDetected = useMemo(() => {
		const firstNameChanged = firstName.trim() !== (userProfile?.firstName || '').trim();
		const lastNameChanged = lastName.trim() !== (userProfile?.lastName || '').trim();
		const photoChanged = !!selectedImage; // !! to convert to boolean - if there is a selected image, it means photo has changed
		return firstNameChanged || lastNameChanged || photoChanged;
	}, [firstName, lastName, userProfile, selectedImage]);

	// Check if there's an actual photo (not just an empty string or placeholder)
	const hasPhoto = useMemo(() => {
		const currentPhoto = previewUrl || photo;
		return currentPhoto && currentPhoto.trim() !== '' && currentPhoto.startsWith('http');
	}, [previewUrl, photo]);

	if (!isOpen) return null;

	// const handleBackdropClick = (e) => {
	//   if (e.target !== e.currentTarget) return

	//   const selection = window.getSelection && window.getSelection()
	//   const hasActiveSelection = selection && selection.toString().length > 0

	//   if (hasActiveSelection) return
	//   if (mouseDownInsideRef.current) return

	//   onClose()
	// }

	const handleFileSelect = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Validate file type
			const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
			if (!allowedTypes.includes(file.type)) {
				Toast.error('Please select a valid image file (JPEG, PNG, or WebP).');
				event.target.value = ''; // Reset file input
				return;
			}

			// Validate file size (1MB = 1024 * 1024 bytes)
			if (file.size > MAX_SIZE_BYTES) {
				Toast.error('Image size must be 1 MB or less.');
				event.target.value = ''; // Reset file input
				return;
			}

			// Read to validate dimensions, then set preview
			const reader = new FileReader();
			reader.onload = (e) => {
				const dataUrl = e.target.result;
				const img = new Image();
				img.onload = () => {
					if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
						Toast.error(
							`Image dimensions must be ${MAX_DIMENSION}×${MAX_DIMENSION} px or smaller.`
						);
						event.target.value = ''; // Reset file input
						return;
					}
					setSelectedImage(file);
					setPreviewUrl(dataUrl);
					event.target.value = ''; // Reset file input for next selection
				};
				img.onerror = () => {
					Toast.error('Could not read the image. Please try another file.');
					event.target.value = ''; // Reset file input
				};
				img.src = dataUrl;
			};
			reader.onerror = () => {
				Toast.error('Failed to load the image file.');
				event.target.value = ''; // Reset file input
			};
			reader.readAsDataURL(file);
		}
	};

	const handleImageUpload = () => {
		fileInputRef.current?.click();
	};

	const handleSave = async () => {
		const nameRegex = /^[A-Za-z\s]+$/;
		const trimmedFirst = firstName.trim();
		const trimmedLast = lastName.trim();

		if (!trimmedFirst) {
			Toast.error('Please enter your first name.');
			return;
		}
		if (trimmedFirst.length < 3) {
			Toast.error('Minimum length for first name is 3 characters.');
			return;
		}
		if (!nameRegex.test(trimmedFirst)) {
			Toast.error('First name can only include letters.');
			return;
		}

		if (!trimmedLast) {
			Toast.error('Please enter your last name.');
			return;
		}
		if (trimmedLast.length < 3) {
			Toast.error('Minimum length for last name is 3 characters.');
			return;
		}
		if (!nameRegex.test(trimmedLast)) {
			Toast.error('Last name can only include letters.');
			return;
		}

		// Pass the selected image file along with other user data
		try {
			setIsSaving(true);
			await Promise.resolve(
				onSaveProfile({
					firstName: trimmedFirst,
					lastName: trimmedLast,
					photo: selectedImage || photo,
					profileImage: selectedImage
				})
			);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div
			className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
			// onClick={handleBackdropClick}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/70" />

			{/* Modal */}
			<div
				className="relative z-10 w-full max-w-[500px] rounded-[30px] bg-white shadow-2xl p-[50px]"
				onMouseDown={() => {
					mouseDownInsideRef.current = true;
				}}
				onMouseUp={() => {
					setTimeout(() => {
						mouseDownInsideRef.current = false;
					}, 0);
				}}
			>
				{/* Close button */}
				<button
					onClick={onClose}
					className="absolute top-3 right-3 sm:top-4 sm:right-4 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				{/* Heading */}
				<div className="flex items-center gap-1 sm:gap-2">
					<h2 className="text-xl sm:text-2xl font-bold text-dark-blue mb-1">Edit profile</h2>
					<Tooltip content="Update your name and profile photo.">
						<InfoTooltip />
					</Tooltip>
				</div>
				<p className="text-gray text-sm sm:text-base mb-8 mt-3">
					Update your name and profile photo.
				</p>

				{/* Profile photo section */}
				<div className="flex sm:flex-row flex-col items-center gap-3 mb-10">
					<div className="relative w-[100px] h-[100px] rounded-full overflow-hidden flex-shrink-0 border border-lightGrey">
						{!isImageLoaded ? (
							<Skeleton
								height="100%"
								width="100%"
								baseColor="#E5E7EB"
								highlightColor="#F3F4F6"
								duration={1.2}
								circle
							/>
						) : (
							<img src={previewUrl || photo} alt="Profile" className="w-full h-full object-cover" />
						)}
					</div>
					<div className="sm:text-left text-center">
						<div className="flex items-center justify-center sm:justify-normal mb-1">
							<p className="text-dark-blue font-medium text-sm">
								{hasPhoto ? 'Change Profile Photo' : 'Upload Profile Photo'}
							</p>
							<Tooltip content="Upload a photo to personalize your profile. Your initials are shown if no photo is added.">
								<InfoTooltip />
							</Tooltip>
						</div>

						<p className="text-[12px] text-gray mb-2 italic">
							Upload a JPG, PNG, OR WebP file (max 1 MB, 1024X1024 px)
						</p>

						<Button
							onClick={handleImageUpload}
							variant="outline-gradient-text"
							className="!w-auto !px-4 h-[30px] rounded-[6px] text-xs font-medium"
						>
							{hasPhoto ? 'Change Profile Photo' : 'Upload Photo'}
						</Button>

						{/* Hidden file input */}
						<input
							ref={fileInputRef}
							type="file"
							accept="image/jpeg,image/png,image/webp"
							onChange={handleFileSelect}
							className="hidden"
						/>
					</div>
				</div>

				{/* Input fields */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-[50px]">
					{/* First Name */}
					<div>
						<div className="flex items-center gap-2 sm:gap-1 mb-2">
							<label htmlFor="first-name" className="text-sm font-medium text-dark-blue">
								First Name <span className="text-red-500">*</span>
							</label>
							<Tooltip content="Enter the first name you want displayed on your account. Letters, spaces, hyphens, and apostrophes only.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<input
							id="first-name"
							type="text"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							placeholder="First Name *"
							className="w-full rounded-[10px] border border-gray px-4 py-3 text-sm focus:ring-1 focus:ring-purple outline-none"
						/>
					</div>

					{/* Last Name */}
					<div>
						<div className="flex items-center gap-2 sm:gap-1 mb-2">
							<label htmlFor="last-name" className="text-sm font-medium text-dark-blue">
								Last Name <span className="text-red-500">*</span>
							</label>
							<Tooltip content="Enter the last name you want displayed on your account. Letters, spaces, hyphens, and apostrophes only.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<input
							id="last-name"
							type="text"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							placeholder="Last Name *"
							className="w-full rounded-[10px] border border-gray px-4 py-3 text-sm focus:ring-1 focus:ring-purple outline-none"
						/>
					</div>

					{/* Email Address (read-only) */}
					<div className="sm:col-span-2">
						<div className="flex items-center gap-2 sm:gap-1 mb-2">
							<label htmlFor="email" className="text-sm font-medium text-[#9CA3AF]">
								Email Address
							</label>
							<Tooltip content="This email is used for login and account notifications.">
								<InfoTooltip className="border-[#9CA3AF] text-[#9CA3AF]" />
							</Tooltip>
						</div>
						<input
							id="email"
							type="email"
							value={email}
							readOnly
							disabled
							placeholder="Email Address"
							className="w-full rounded-[10px] border border-lightGrey px-4 py-3 text-sm bg-gray-100 text-[#9CA3AF] placeholder-[#9CA3AF]"
						/>
					</div>
				</div>

				{/* Buttons */}
				<div className="flex gap-3">
					<Tooltip
						content="Discard your changes and return to profile settings."
						wrapperClassName="flex-1"
					>
						<Button
							onClick={onClose}
							variant="outline-gradient-text"
							className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-sm sm:text-base"
						>
							Cancel
						</Button>
					</Tooltip>
					<Tooltip content="Save your updated profile details." wrapperClassName="flex-1">
						<Button
							onClick={handleSave}
							disabled={isSaving || !hasChangesDetected}
							className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-white text-sm sm:text-base"
						>
							{isSaving ? 'Updating...' : 'Save Changes'}
						</Button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
}
